import React, { useContext, useEffect, useRef, useState } from 'react'
import API from '../../../services/API'
import { useNavigate } from 'react-router-dom'
import { GlobalContext } from '../../../App'
import PermissionCheck from '../../../helpers/PermissionCheck'
import TipTap from '../../../components/TipTap/TipTap'
import { ANNOUNCEMENT_TYPES, typeColor } from './announcementConstants'

// @MUI
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'

const fileIconFor = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase()
  if (['jpeg', 'jpg', 'png', 'gif', 'webp', 'jfif', 'heic'].includes(ext))
    return <ImageOutlinedIcon fontSize="small" color="primary" />
  if (['mp4', 'mov', 'm4v', 'webm'].includes(ext))
    return <VideocamOutlinedIcon fontSize="small" color="primary" />
  if (ext === 'pdf')
    return <PictureAsPdfOutlinedIcon fontSize="small" color="error" />
  return <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
}

const AttachmentRow = ({ name, onRemove }) => (
  <Stack
    direction="row"
    gap={1}
    alignItems="center"
    justifyContent="space-between"
    sx={{
      p: 1,
      borderRadius: 2,
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
      {fileIconFor(name)}
      <Typography variant="body2" noWrap>
        {name}
      </Typography>
    </Stack>
    <Tooltip title="Remove">
      <IconButton size="small" color="error" onClick={onRemove}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
)

const AddNew = ({ existingAnnouncement, onCancel }) => {
  const [type, setType] = useState('')
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [requiresAck, setRequiresAck] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [existingAttachments, setExistingAttachments] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const fileInput = useRef()

  useEffect(() => {
    if (existingAnnouncement) {
      setType(existingAnnouncement.type)
      setTitle(existingAnnouncement.title)
      setDetail(existingAnnouncement.detail)
      setRequiresAck(existingAnnouncement.requiresAcknowledgement)
      setExistingAttachments(existingAnnouncement.attachments ?? [])
    }
  }, [existingAnnouncement])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!type) {
      setToast({ message: 'Please select a type', severity: 'error' })
      return
    }
    setSubmitting(true)
    const syncToDb = (s3keys) => {
      if (existingAnnouncement) {
        API.patch(`/governance/announcement/${existingAnnouncement._id}`, {
          title,
          detail,
          type,
          requiresAcknowledgement: requiresAck,
          attachments: s3keys,
        })
          .then((res) => {
            if (res.status === 200) {
              setToast({ message: 'Announcement Updated' })
              navigate('/announcements')
            } else {
              setToast({
                message: 'Unable to edit Announcement. Please try again later',
                severity: 'error',
              })
            }
          })
          .catch((e) => console.error(e))
          .finally(() => setSubmitting(false))
      } else {
        API.post('/governance/announcement/new', {
          title,
          detail,
          type,
          requiresAcknowledgement: requiresAck,
          attachments: s3keys,
        })
          .then((res) => {
            if (res.status === 200) {
              setToast({ message: 'Announcement Created' })
              navigate('/announcements')
            } else {
              setToast({
                message:
                  'Unable to create Announcement. Please try again later',
                severity: 'error',
              })
            }
          })
          .catch((e) => console.log(e))
          .finally(() => setSubmitting(false))
      }
    }
    try {
      const uploadAttachments = attachments.map(async (attachment) => {
        // Derive the extension from the filename, not the MIME type —
        // video/quicktime would otherwise store a .quicktime key
        const extension = attachment.name.split('.').pop()?.toLowerCase()
        const res = await API.post('/gets3uploadurl', {
          folder: 'governance/announcements',
          extension,
        })
        await API.put(res.data.url, attachment, {
          headers: {
            'Content-Type': attachment.type || 'application/octet-stream',
          },
        })
        return res.data.key
      })

      const newKeys = await Promise.all(uploadAttachments)
      syncToDb([...existingAttachments, ...newKeys])
    } catch (err) {
      console.error(err)
      setToast({
        message: 'Unable to upload attachments. Please try again later',
        severity: 'error',
      })
      setSubmitting(false)
    }
  }

  // Mirrors the server's reset rule: content changes (or newly requiring
  // acknowledgement) clear the read/acknowledgement record
  const willResetAcknowledgements =
    !!existingAnnouncement &&
    (title !== existingAnnouncement.title ||
      detail !== existingAnnouncement.detail ||
      attachments.length > 0 ||
      existingAttachments.length !==
        (existingAnnouncement.attachments ?? []).length ||
      (requiresAck && !existingAnnouncement.requiresAcknowledgement))

  const handleDeleteAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDeleteExistingAttachment = (index) => {
    setExistingAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <PermissionCheck roles={['Manager', 'Supervisor', 'Governance']} showAlert>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack gap={2} component="form" onSubmit={handleSubmit}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Typography variant="title2">
              {existingAnnouncement ? 'Edit' : 'New'} Announcement
            </Typography>
            {onCancel && (
              <Tooltip title="Cancel editing">
                <IconButton size="small" onClick={onCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            label="Title"
            size="small"
            required
            autoComplete="off"
          />

          <Stack gap={0.75}>
            <Typography variant="caption" color="text.secondary">
              Type *
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
              {ANNOUNCEMENT_TYPES.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  clickable
                  color={type === option ? typeColor(option) : 'default'}
                  variant={type === option ? 'filled' : 'outlined'}
                  onClick={() => setType(option)}
                  sx={{ borderRadius: 999, fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Stack>

          <TipTap value={detail} onChange={(newValue) => setDetail(newValue)} />

          <Stack
            gap={1}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography fontWeight={700} fontSize="0.95rem">
                Attachments
              </Typography>
              <Button
                variant="outlined"
                startIcon={<NoteAddIcon />}
                size="small"
                onClick={() => fileInput.current.click()}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Add File
                <input
                  ref={fileInput}
                  type="file"
                  style={{ display: 'none' }}
                  accept="application/pdf,.doc,.docx,image/*,video/*"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) setAttachments((prev) => [...prev, file])
                    e.target.value = ''
                  }}
                />
              </Button>
            </Stack>
            {existingAttachments.length === 0 && attachments.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Attach documents, images or videos — staff can view them
                directly on the announcement.
              </Typography>
            )}
            {existingAttachments.map((key, index) => (
              <AttachmentRow
                key={key}
                name={key.split('/').pop()}
                onRemove={() => handleDeleteExistingAttachment(index)}
              />
            ))}
            {attachments.map((attachment, index) => (
              <AttachmentRow
                key={`${attachment.name}-${index}`}
                name={attachment.name}
                onRemove={() => handleDeleteAttachment(index)}
              />
            ))}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={requiresAck}
                onChange={(e) => setRequiresAck(e.target.checked)}
              />
            }
            label="Does this notice require Acknowledgement by staff?"
          />

          {willResetAcknowledgements && (
            <Alert severity="warning">
              Saving these changes will clear the existing read /
              acknowledgement record — all staff will be asked to{' '}
              {requiresAck ? 'acknowledge' : 'read'} this announcement again.
            </Alert>
          )}

          {submitting && <LinearProgress />}

          <Stack direction="row" gap={1} justifyContent="flex-end">
            {onCancel && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCancel}
                disabled={submitting}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={submitting}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              {existingAnnouncement ? 'Save Changes' : 'Publish Announcement'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </PermissionCheck>
  )
}

export default AddNew
